<template>
  <Teleport to="body" v-if="isVisible">
    <div class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <div class="modal-header">
          <h6 class="mb-0">{{ modalTitle }}</h6>
          <i class="bi bi-x-circle close-icon" @click="close"></i>
        </div>

        <div class="modal-content">
          <div v-if="loading" class="text-center py-3">Carregando…</div>
          <div v-else>
            <div v-if="erro" class="alert alert-danger">{{ erro }}</div>

            <div class="mb-2">
              <strong>Para: </strong>
                  <span> {{ displayTo }} </span>
            </div>
            <div class="mb-2"><strong>Assunto:</strong> {{ modalTitle }}</div>

            <div class="mb-3">
              <div class="ql-editor border rounded p-3" v-safe-html="templateProcessado"></div>
            </div>
          </div>
        </div>

        <div class="modal-footer d-flex justify-content-end gap-2">
          <button class="btn btn-secondary btn-sm" @click="close">Cancelar</button>
          <button
            class="btn btn-primary btn-sm"
            :disabled="readOnly || isSending || !emailTo"
            @click="enviar"
          >
            {{ isSending ? 'Enviando…' : 'Enviar e-mail' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { sanitizeHtml, renderHtmlTemplate } from '@/utils/safeHtml';
import { ref, computed, watch, getCurrentInstance } from 'vue'
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'

/**
 * Props: mesmo padrão do EmailAniversario.vue
 * - visible: controla abertura
 * - registro: item vindo do card do Dashboard
 *   Esperado: { matricula_id, aluno_nome, dias_para_vencer, data_vencimento,
 *               email_responsavel, responsavel_id, responsavel_nome? }
 * - readOnly: desabilita envio
 */
const props = defineProps({
  // aceita os dois jeitos
  visible:  { type: Boolean, default: false },
  show:     { type: Boolean, default: undefined }, // alias
  registro: { type: Object,  default: null },
  readOnly: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'sent'])

/* ======= estados ======= */
const loading             = ref(false)
const isSending           = ref(false)
const erro                = ref('')
const empresa             = ref({ nome: '' })
const templateRaw         = ref('')
const templateProcessado  = ref('')

/* ======= helpers ======= */
const { proxy } = getCurrentInstance()
const userName  = proxy?.$globalState?.userName
              || proxy?.$store?.state?.auth?.user?.nome
              || 'sistema'

const documentoTitulo = computed(() => 'E-mail de Renovação Antecipada')

const modalTitle = computed(() => {
  const aluno = (props?.registro?.aluno_nome || '').trim() || '—'
  const dias  = props?.registro?.dias_para_vencer ?? '—'
  return `Renovação antecipada — Matrícula do(a) aluno(a) ${aluno} vence em ${dias} dias`
})

const emailTo = computed(() => props.registro?.email_responsavel || '')


function inlineQuillForEmail(html) {
  const root = document.createElement('div')
  root.innerHTML = sanitizeHtml(html)

  // ---- Alinhamentos (já existiam) ----
  root.querySelectorAll('.ql-align-center').forEach(el => { el.style.textAlign = 'center'; el.classList.remove('ql-align-center') })
  root.querySelectorAll('.ql-align-right').forEach(el  => { el.style.textAlign = 'right';  el.classList.remove('ql-align-right') })
  root.querySelectorAll('.ql-align-justify').forEach(el=> { el.style.textAlign = 'justify';el.classList.remove('ql-align-justify') })

  // ---- Tamanhos (já existiam) ----
  root.querySelectorAll('.ql-size-small').forEach(el => { el.style.fontSize = '0.8em';  el.classList.remove('ql-size-small') })
  root.querySelectorAll('.ql-size-large').forEach(el => { el.style.fontSize = '1.5em'; el.classList.remove('ql-size-large') })
  root.querySelectorAll('.ql-size-huge').forEach(el  => { el.style.fontSize = '2em';   el.classList.remove('ql-size-huge') })

  // ---- Fontes (já existiam) ----
  root.querySelectorAll('.ql-font-serif').forEach(el      => { el.style.fontFamily = 'Georgia, Times, "Times New Roman", serif'; el.classList.remove('ql-font-serif') })
  root.querySelectorAll('.ql-font-monospace').forEach(el => { el.style.fontFamily = '"Courier New", Courier, monospace';        el.classList.remove('ql-font-monospace') })

  // ---- Recuos (já existiam) ----
  for (let i = 1; i <= 9; i++) {
    root.querySelectorAll(`.ql-indent-${i}`).forEach(el => { el.style.marginLeft = `${i * 3}em`; el.classList.remove(`ql-indent-${i}`) })
  }

  // ========== NOVO: espaçamento consistente ==========
  const LH_BODY = '1.6'     // line-height do corpo
  const LH_H    = '1.25'    // line-height de títulos
  const P_MB    = '8px'     // margin-bottom de parágrafos
  const LI_MB   = '6px'     // margin-bottom de itens de lista
  const LIST_PL = '20px'    // recuo de lista

  // Parágrafos
  root.querySelectorAll('p').forEach(el => {
    el.style.margin = `0 0 ${P_MB} 0`
    el.style.lineHeight = LH_BODY
    el.style.setProperty('mso-line-height-rule', 'exactly') // ajuda no Outlook
  })

  // Títulos
  root.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach(el => {
    el.style.margin = '0 0 12px 0'
    el.style.lineHeight = LH_H
    el.style.setProperty('mso-line-height-rule', 'exactly')
  })

  // Listas (ol/ul) e itens
  root.querySelectorAll('ol,ul').forEach(el => {
    el.style.margin = '0 0 12px 0'
    el.style.padding = `0 0 0 ${LIST_PL}`
    el.style.lineHeight = LH_BODY
    el.style.setProperty('mso-line-height-rule', 'exactly')
  })
  root.querySelectorAll('li').forEach(el => {
    el.style.margin = `0 0 ${LI_MB} 0`
    el.style.lineHeight = LH_BODY
    el.style.setProperty('mso-line-height-rule', 'exactly')
  })

  // (Opcional) links sem azul/sublinhado padrão:
  // root.querySelectorAll('a').forEach(a => { a.style.color = 'inherit'; a.style.textDecoration = 'none' })

  return root.innerHTML
}



/* ======= datas (calc D-20 e D-10) ======= */
function toDate(d) {
  // aceita 'YYYY-MM-DD' ou Date
  if (!d) return null
  const dt = d instanceof Date ? d : new Date(String(d))
  return isNaN(dt) ? null : dt
}
function addDays(dt, days) {
  const x = new Date(dt.getTime())
  x.setDate(x.getDate() + days)
  return x
}
function fmtBR(dStr) {
  if (!dStr) return ''
  const asDate = toDate(dStr)
  if (!asDate) return String(dStr)

  const hasFormatter = globalUtils && typeof globalUtils.formatarData === 'function'
  if (hasFormatter) {
    try {
      const iso = asDate.toISOString().slice(0, 10)
      return globalUtils.formatarData(iso)
    } catch (e) {
      // Ignora erro no formatarData e usa o fallback abaixo
    }
  }

  return asDate.toLocaleDateString('pt-BR')
}

/* Campos de substituição do documento */
const dataVenc        = computed(() => props.registro?.data_vencimento || props.registro?.data_fim || null)
const dataVencMenos20 = computed(() => {
  const base = toDate(dataVenc.value)
  return base ? addDays(base, -20) : null
})
const dataVencMenos10 = computed(() => {
  const base = toDate(dataVenc.value)
  return base ? addDays(base, -10) : null
})

/* ======= carregar empresa e documento ======= */
async function carregarEmpresa() {
  const { data } = await axios.get('/configuracoes/empresa')
  // ajuste se sua API envolver { data: {...} }
  empresa.value = data?.data || data || {}
}

async function carregarDocumento() {
  const { data } = await axios.get('/configuracoes/documentos')
  const lista = data?.data || data || []
  // procura por título exato (mesmo comportamento do modal de aniversário)
  const doc = lista.find(d =>
    (d.titulo || d.title || '').trim().toLowerCase() === documentoTitulo.value.toLowerCase()
  )
  if (!doc) {
    throw new Error(`Modelo não encontrado: ${documentoTitulo.value}`)
  }
  templateRaw.value = doc.conteudo || doc.content || ''
}

/* ======= substituições ======= */
function processarTemplate() {
  const r = props.registro || {}
  const subs = {
    nome_aluno:               r.aluno_nome || '',
    nome_responsavel:         r.responsavel_nome || '',
    email:                    r.email_responsavel || '',
    nome_escola:              empresa.value?.nome || '',
    matricula_id:             r.matricula_id ?? '',
    dias_para_vencer:         r.dias_para_vencer ?? '',
    data_vencimento:          dataVenc.value ? fmtBR(dataVenc.value) : '',
    data_vencimento_menos_20: dataVencMenos20.value ? fmtBR(dataVencMenos20.value) : '',
    data_vencimento_menos_10: dataVencMenos10.value ? fmtBR(dataVencMenos10.value) : '',
  }

  const htmlSubstituido = renderHtmlTemplate(templateRaw.value, subs)
  // >>> transforma classes do Quill em estilos inline (compatível com e-mail)
  templateProcessado.value = inlineQuillForEmail(htmlSubstituido)
}

/* ======= ciclo de abertura ======= */
async function abrir() {
  loading.value = true
  erro.value = ''
  templateRaw.value = ''
  templateProcessado.value = ''
  try {
    await carregarEmpresa()
    await carregarDocumento()
    processarTemplate()
  } catch (e) {
    console.error(e)
    erro.value = e?.message || 'Falha ao carregar o modelo.'
  } finally {
    loading.value = false
  }
}

function close() {
  emit('close')
}

/* ======= envio ======= */
async function enviar() {
  if (isSending.value || !emailTo.value) return
  isSending.value = true
  erro.value = ''
  try {
    // Garante estilos inline compatíveis com clientes de e-mail
    const htmlEmail = inlineQuillForEmail(templateProcessado.value || '')

    const payload = {
    pessoa_fisica_id:   props.registro?.responsavel_id ?? null,
    destinatario_email: emailTo.value,
    destinatario_nome:  (props.registro?.responsavel_nome || '').trim(),
    tipo:      'renovacao_antecipada',
    template:  documentoTitulo.value,
    assunto:   modalTitle.value,
    corpo:     htmlEmail,
    por:       userName,
    // >>> NOVOS CAMPOS PARA O CONTROLE <<<
    matricula_id:        props.registro?.matricula_id ?? null,
    ref_data_vencimento: props.registro?.data_vencimento || null  // 'YYYY-MM-DD'
    }

    await axios.post('/operacional/emails/enviar-renovacao-antecipada', payload)
    emit('sent', props.registro) // manda de volta qual registro foi enviado
    close()
  } catch (e) {
    console.error('Falha ao enviar e-mail de renovação antecipada:', e)
    erro.value = 'Não foi possível enviar o e-mail agora.'
  } finally {
    isSending.value = false
  }
}

const displayTo = computed(() => {
  const nome  = props.registro?.responsavel_nome?.trim()
  const email = emailTo.value?.trim()
  if (nome && email) return `${nome} <${email}>`
  if (email) return email
  if (nome)  return nome
  return '—'
})

const isVisible = computed(() => (props.show !== undefined ? props.show : props.visible))

/* ======= reatividade na abertura ======= */
watch(() => isVisible.value, (v) => { if (v) abrir() })

</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Container flex com altura limitada */
.modal-container {
  background: #fff;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  border-radius: 4px;
  z-index: 2001;
}

/* Cabeçalho e rodapé fixos */
.modal-header {
  background: #ccc;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 0 0 auto;            /* não cresce */
}

.modal-footer {
  padding: 10px 15px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 0 auto;            /* não cresce */
}

/* Corpo com scroll */
.modal-content {
  padding: 15px;
  flex: 1 1 auto;            /* ocupa o espaço restante */
  overflow-y: auto;          /* scroll vertical no corpo */
  -webkit-overflow-scrolling: touch; /* suaviza em mobile */
}

/* Ícone de fechar */
.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}

/* Botões (mesmo visual base) */
.btn {
  min-width: 70px;
  padding: 6px 15px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}

.ql-editor {
  min-height: 220px;
  /* mantém a aparência parecida com o EmailAniversario.vue */
}
</style>
